import React, { useState, useEffect, useRef } from 'react';
import toast, { Toaster } from "react-hot-toast";
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import RightSidebar from './components/RightSidebar';
import api from '../connector/api';

// Tab Components
import GeneralTab from './components/tabs/GeneralTab';
import AttendanceTab from './components/tabs/AttendanceTab';
import LeaveRulesTab from './components/tabs/LeaveRuleTab';
import SecurityTab from './components/tabs/SecurityTab';
import AcademicCalendarTab from './components/tabs/AcademicCalendarTab';
import GradingSchemaTab from './components/tabs/GradeSchemaTab';
import GradeCategoryTab from './components/tabs/GradeCategoryTab';

export default function ConfigurationPage() {
    const [activeTab, setActiveTab] = useState('general');
    const [isSaving, setIsSaving] = useState(false);
    
    // Control RightSidebar Visibility (Silent toggle without toasts)
    const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(true);

    const toggleRightSidebar = () => {
        setIsRightSidebarOpen((prev) => !prev);
    };

    // Header & Sidebar Shell States
    const [permissions, setPermissions] = useState([]);
    const [user, setUser] = useState({ first_name: "Admin", last_name: "User", email: "admin@school.edu" });
    const [assignedClassesList, setAssignedClassesList] = useState([]);
    const [notifOpen, setNotifOpen] = useState(false);
    const [currentPeriod, setCurrentPeriod] = useState(null);
    const [loadingPeriod, setLoadingPeriod] = useState(true);
    const notificationDropdownRef = useRef(null);

    // Initial Layout Data Fetch
    useEffect(() => {
        const storedPerms = sessionStorage.getItem("permissions");
        const storedUser = sessionStorage.getItem("user");
        if (storedPerms) { try { setPermissions(JSON.parse(storedPerms)); } catch (e) {} }
        if (storedUser) { try { setUser(JSON.parse(storedUser)); } catch (e) {} }

        const fetchInitialData = async () => {
            try {
                setLoadingPeriod(true);
                const [periodRes, subjectsRes] = await Promise.all([
                    api.get("year-terms/get-set-year-term/"),
                    api.get("teacher/subjects/").catch(() => ({ data: [] }))
                ]);
                setCurrentPeriod(periodRes.data);
                setAssignedClassesList(subjectsRes.data.results || subjectsRes.data || []);
            } catch (err) {
                console.error("Failed to load header metadata:", err);
            } finally {
                setLoadingPeriod(false);
            }
        };
        fetchInitialData();
    }, []);

    const handleLogout = () => {
        sessionStorage.clear();
        localStorage.clear();
        window.location.href = "/login";
    };

    const yearDisplay = currentPeriod?.school_year?.name || currentPeriod?.school_year_detail?.name || "";
    const termDisplay = currentPeriod?.term?.name || currentPeriod?.term_detail?.name || "";

    // General Tab States & Logic
    const [schoolConfig, setschoolConfigs] = useState({
        name: "", school_code: "", address: "", city: "", state: "", 
        postal_code: "", country: "", phone: "", email: "", website: "", 
        principal_name: "", timezone: "UTC", school_logo: ""
    });
    const [logoPreview, setLogoPreview] = useState(null);

    useEffect(() => {
        const fetchSchoolConfig = async () => {
            try {
                const res = await api.get('settings/general/');
                if (Array.isArray(res.data) && res.data.length > 0) {
                    const config = res.data[0];
                    setschoolConfigs(config);
                    if (config.school_logo) setLogoPreview(config.school_logo);
                } else if (res.data && !Array.isArray(res.data) && res.data.id) {
                    setschoolConfigs(res.data);
                    if (res.data.school_logo) setLogoPreview(res.data.school_logo);
                }
            } catch (err) {
                console.error("Failed to fetch school configuration:", err);
            }
        };
        fetchSchoolConfig();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setschoolConfigs((prev) => ({ ...prev, [name]: value }));
    };

    const handleSaveGeneral = async (e) => {
        if (e) e.preventDefault();
        setIsSaving(true);
        try {
            const payload = new FormData();
            Object.keys(schoolConfig).forEach((key) => {
                if (key !== 'school_logo' && schoolConfig[key] !== null) {
                    payload.append(key, schoolConfig[key]);
                }
            });

            const logoInput = document.querySelector('input[name="school_logo"]');
            if (logoInput && logoInput.files[0]) {
                payload.append('school_logo', logoInput.files[0]);
            }

            let response = schoolConfig.id 
                ? await api.put(`settings/general/${schoolConfig.id}/`, payload, { headers: { 'Content-Type': 'multipart/form-data' } })
                : await api.post('settings/general/', payload, { headers: { 'Content-Type': 'multipart/form-data' } });

            setschoolConfigs(response.data);
            if (response.data.school_logo) setLogoPreview(response.data.school_logo);
            toast.success("Institution configurations synchronized successfully!");
        } catch (err) {
            console.error("Failed to save configuration profile:", err);
            toast.error("Error saving workspace parameters.");
        } finally {
            setIsSaving(false);
        }
    };

    // Attendance Tab States & Logic
    const [attendanceRules, setAttendanceRules] = useState({ 
        name: "", 
        max_absences: 0, 
        max_tardies: 0, 
        notification_enabled: true 
    });
    const [attendanceCodes, setAttendanceCodes] = useState([]);

    const fetchAttendanceData = async () => {
        try {
            const rulesRes = await api.get('settings/attendance/rule/');
            if (rulesRes.data && rulesRes.data.length > 0) {
                setAttendanceRules(rulesRes.data[0]);
            }

            const codesRes = await api.get('settings/attendance/code/');
            setAttendanceCodes(codesRes.data || []);
        } catch (err) {
            console.error("Failed to load attendance configurations:", err);
        }
    };

    useEffect(() => {
        fetchAttendanceData();
    }, []);

    const handleRuleChange = (e) => {
        const { name, value } = e.target;
        setAttendanceRules(prev => ({ ...prev, [name]: value }));
    };

    const handleCodeChange = (index, field, value) => {
        setAttendanceCodes(prev => {
            const updated = [...prev];
            if (field === 'is_present' && value === true) {
                updated[index].is_absent = false;
            } else if (field === 'is_absent' && value === true) {
                updated[index].is_present = false;
                updated[index].is_tardy = false;
            }
            updated[index][field] = value;
            return updated;
        });
    };

    const handleAddCode = () => {
        setAttendanceCodes(prev => [...prev, { code: "", description: "", is_present: false, is_absent: false, is_tardy: false }]);
    };

    const handleDeleteCode = async (index) => {
        const codeToDelete = attendanceCodes[index];
        setAttendanceCodes(prev => prev.filter((_, i) => i !== index));

        if (codeToDelete.id) {
            try {
                await api.delete(`settings/attendance/code/${codeToDelete.id}/`);
            } catch (err) {
                console.error("Failed to delete database record:", err);
                fetchAttendanceData();
            }
        }
    };

    const handleSaveAttendance = async () => {
        setIsSaving(true);
        try {
            if (attendanceRules.id) {
                await api.put(`settings/attendance/rule/${attendanceRules.id}/`, attendanceRules);
            } else {
                await api.post('settings/attendance/rule/', attendanceRules);
            }

            const codePromises = attendanceCodes.map(item => {
                if (item.id) {
                    return api.put(`settings/attendance/code/${item.id}/`, item);
                } else {
                    return api.post('settings/attendance/code/', item);
                }
            });
            await Promise.all(codePromises);

            toast.success("Attendance configurations synchronized successfully!");
            fetchAttendanceData(); 
        } catch (err) {
            console.error("Error saving data:", err);
            toast.error("Failed to save changes.");
        } finally {
            setIsSaving(false);
        }
    };

    // Leave Rules Tab States & Logic
    const LEAVE_TYPES = [
        "Personal Leave",
        "Compassionate Leave",
        "Emergency Leave",
        "Leave Without Pay",
        "Sick Leave"
    ];

    const [leaveRules, setLeaveRules] = useState(
        LEAVE_TYPES.map(type => ({
            leave_type: type,
            max_allowed_days: 0,
            is_paid: type !== "Leave Without Pay"
        }))
    );
    const [isSavingRules, setIsSavingRules] = useState(false);

    const fetchLeaveRules = async () => {
        try {
            const res = await api.get('settings/leave-rules/');
            const rulesArray = res.data?.results || [];

            const masterList = LEAVE_TYPES.map(type => {
                const match = rulesArray.find(r => r.leave_type === type);
                return {
                    leave_type: type,
                    max_allowed_days: match ? Number(match.max_allowed_days) : 0,
                    is_paid: match && match.is_paid !== undefined ? match.is_paid : type !== "Leave Without Pay"
                };
            });
            setLeaveRules(masterList);
        } catch (err) {
            console.error("Failed fetching leave configurations:", err);
        }
    };

    useEffect(() => {
        fetchLeaveRules();
    }, []);

    const handleRuleDaysChange = (index, value) => {
        setLeaveRules(prev => {
            const copy = [...prev];
            copy[index] = {
                ...copy[index],
                max_allowed_days: value === "" ? 0 : parseInt(value, 10)
            };
            return copy;
        });
    };

    const handleRuleToggleChange = (index, value) => {
        setLeaveRules(prev => {
            const copy = [...prev];
            copy[index] = {
                ...copy[index],
                is_paid: value
            };
            return copy;
        });
    };

    const handleSaveRules = async () => {
        setIsSavingRules(true);
        try {
            await api.post('settings/leave-rules/save-batch/', { rules: leaveRules });
            toast.success("Leave management limits successfully saved!");
            fetchLeaveRules();
        } catch (err) {
            console.error(err);
            toast.error("An error occurred executing rule alignment parameters.");
        } finally {
            setIsSavingRules(false);
        }
    };

    return (
        <div className="flex h-screen w-full bg-zinc-100  text-zinc-800 antialiased overflow-hidden select-none">
            <Toaster position="top-right" />
            
            <Sidebar
                permissions={permissions}
                user={user}
                teacherSubjects={assignedClassesList}
                handleLogout={handleLogout}
            />

            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                <Header
                    loading={loadingPeriod}
                    loadingPeriod={loadingPeriod}
                    yearDisplay={yearDisplay}
                    termDisplay={termDisplay}
                    notifOpen={notifOpen}
                    setNotifOpen={setNotifOpen}
                    notificationDropdownRef={notificationDropdownRef}
                />

                <main className="flex-1 overflow-y-auto p-6 custom-scroll font-dynamic-title">
                    {/* Constrained layout width */}
                    <div className="max-w-7xl mx-auto space-y-6">
                        
                        <div className="bg-white border border-zinc-200 rounded-lg p-3 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-sm font-bold text-zinc-900 tracking-tight mt-2">
                                    System Configuration
                                </h1>
                                <p className="text-xs text-zinc-500 mt-0.5">
                                    Configure global rules, attendance compliance matrices, and administrative security configurations.
                                </p>
                            </div>
                            <div>
                                <button
                                    onClick={toggleRightSidebar}
                                    className="px-3 py-1.5 text-xs font-medium text-zinc-700 bg-zinc-50 border border-zinc-300 rounded-lg hover:bg-zinc-100 transition-colors"
                                >
                                    {isRightSidebarOpen ? "Hide Panel" : "Show Panel"}
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                            
                            {/* Main Active Tab View (Dynamically spans 12 or 8 columns) */}
                            <div className={isRightSidebarOpen ? "lg:col-span-8 space-y-6" : "lg:col-span-12 space-y-6"}>
                                {activeTab === 'general' && (
                                    <GeneralTab 
                                        schoolConfig={schoolConfig}
                                        logoPreview={logoPreview}
                                        handleInputChange={handleInputChange}
                                        setLogoPreview={setLogoPreview}
                                        handleSave={handleSaveGeneral}
                                        isSaving={isSaving}
                                    />
                                )}

                                {activeTab === 'academic' && (
                                    <AcademicCalendarTab />
                                )}

                                {activeTab === 'grade' && (
                                    <GradingSchemaTab />
                                )}

                                {activeTab === 'category' && (
                                    <GradeCategoryTab />
                                )}

                                {activeTab === 'attendance' && (
                                    <AttendanceTab 
                                        attendanceRules={attendanceRules}
                                        attendanceCodes={attendanceCodes}
                                        handleRuleChange={handleRuleChange}
                                        handleCodeChange={handleCodeChange}
                                        handleAddCode={handleAddCode}
                                        handleDeleteCode={handleDeleteCode}
                                        handleSave={handleSaveAttendance}
                                        isSaving={isSaving}
                                    />
                                )}

                                {activeTab === 'leave' && (
                                    <LeaveRulesTab
                                        leaveRules={leaveRules}
                                        handleRuleDaysChange={handleRuleDaysChange}
                                        handleRuleToggleChange={handleRuleToggleChange}
                                        handleSaveRules={handleSaveRules}
                                        isSaving={isSavingRules}
                                    />
                                )}

                                {activeTab === 'security' && (
                                    <SecurityTab />
                                )}
                            </div>

                            {/* Right Sidebar Component */}
                            {isRightSidebarOpen && (
                                <RightSidebar 
                                    activeTab={activeTab} 
                                    setActiveTab={setActiveTab} 
                                    onClose={toggleRightSidebar}
                                />
                            )}

                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}